import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Flex, Popover, Input,Button, Switch, Portal, createListCollection, Select,Table, Heading, Text, List, Box, Code } from '@chakra-ui/react';
import { LuTrash2 as LuRemove, LuUsers, LuUserPlus } from "react-icons/lu";
import PageTitle from 'components/ui/PageTitle';
import PageContent from 'components/ui/PageContent';

function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState(createListCollection({items:[]}));
  const [projectNames, setProjectNames] = useState({});
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get('/api/users');
        setUsers(response.data);
        console.log(response.data);
      } catch (err) {
        console.error('Error fetching users', err);
        setError(err.message);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get('/api/projects');
        console.log("projects", response.data);
        const _projectNames = response.data.reduce((acc, p) => {
          acc[p.id] = p.display_name;
          return acc;
        }, {});
        setProjectNames(_projectNames);
        const projects = response.data.map((p) => p.id);
        const projColl = createListCollection({
          items: projects ?? [],
          itemToString: (project) => _projectNames[project],
          itemToValue: (project) => project,
        });
        setProjects(projColl);
        console.log("projColl", projColl);
      } catch (err) {
        console.error('Error fetching projects', err);
        setError(err.message);
      }
    };

    fetchProjects();
  }, []);

  if (error) {
    return <div>Error: {error}</div>;
  }

  const handleUserProjectChange = async (userIndex, items) => {
    try {
      //remove null or undefined values
      items = items.filter(function (el) {
        return el != null;
      });

      const updatedUsers = users.map((u, index) => 
        index === userIndex ? { ...u, visible_projects: items } : u
      );
      // users[userIndex] = user;
      setUsers(updatedUsers);
      const user = updatedUsers[userIndex];
      console.log('User projects updated:', user.visible_projects);

      await axios.patch(
        `/users/${user.id}`,
        {
            visible_projects: user.visible_projects,
        }
      )
      .then((response) => console.log(response))
      .catch((error) => console.log(error));
    } catch (err) {
      console.error('Error updating user projects', err);
      setError(err.message);
    }
  };

  const handleSuperuserChange = async (userIndex, isSuper) => {
    try {
      const _user = users[userIndex];
      if(_user.id === localStorage.getItem("user_id")){
        window.alert("You cannot change your own superuser status");
        return;
      }

      if(isSuper && !window.confirm(`Are you sure you want to make user ${_user.email} a superuser?`)) {
        return;
      }

      const updatedUsers = users.map((u, index) => 
        index === userIndex ? { ...u, is_superuser: isSuper ? "True" : "False" } : u
      );
      setUsers(updatedUsers);
      const user = updatedUsers[userIndex];
  
      await axios.patch(
        `/users/${user.id}`,
        {
          is_superuser: user.is_superuser,
        }
      )
      .then((response) => console.log(response))
      .catch((error) => console.log(error));
    } catch (err) {
      console.error('Error updating superuser status', err);
      setError(err.message);
    }
  };

  const handleDelete = async (e, userIndex) => {
    e.stopPropagation(); // Prevent row click navigation
    const user = users[userIndex];

    if (!window.confirm(`Are you sure you want to delete user ${user.email}?`)) {
        return;
    }
    try {

      // Remove the deleted user from the state
      setUsers(users.filter(u => u.id !== user.id));

      await axios.delete(
        `/users/${user.id}`,
      )
      .then((response) => console.log(response))
      .catch((error) => console.log(error));
    } catch (err) {
      console.error('Error deleting user', err);
    }
  };

  const handleCreateUser = async () => {
    try {
      const response = await axios.post('/auth/register', {
        email: newUserEmail,
        password: newUserPassword,
      });
      
      // Add the new user to the users list
      setUsers([...users, response.data]);
      
      // Clear the form and close popover
      setNewUserEmail('');
      setNewUserPassword('');
      // focus the first element in the page:
      // document.querySelector('div').click();
      setIsOpen(false);
    } catch (err) {
      console.error('Error creating user', err);
      window.alert(`Failed to create user: ${err.message} ${err.response?.data?.detail[0]?.ctx?.reason || ""}`);
    }
  };

  function ProjectSelect({ userIndex  }) {
    const user = users[userIndex];
    if(user.visible_projects === null) {
      return <Text></Text>;
    }
    console.log("projects", user.email, user.visible_projects);
    if(user.is_superuser === "True"){
      // user.visible_projects = projects.items;
      return <Text>All (Superuser)</Text>;
    }
    return(
      <Select.Root collection={projects} size="sm" 
        minW="10em"
        my="-0.5em"
        multiple={projects.items.length > 1}
        value={users[userIndex].visible_projects}
        onValueChange={(e) => handleUserProjectChange(userIndex, e.items)}
        closeOnSelect={projects.items.length <= 1}
      >
        <Select.HiddenSelect />
        <Select.Control>
          <Select.Trigger>
            <Select.ValueText placeholder="Select projects" />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.ClearTrigger />
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <Portal>
          <Select.Positioner>
            <Select.Content>
              {projects.items.map((project) => (
                <Select.Item item={project} key={project}>
                  {projectNames[project] || project}
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>
    );
  }

  function SuperuserToggle({ userIndex }) {
    const user = users[userIndex];
    return (
      <Switch.Root
        size="md"
        my="-1em"
        checked={users[userIndex].is_superuser === "True"}
        onCheckedChange={(e) => handleSuperuserChange(userIndex, e.checked)}
      >
        <Switch.HiddenInput />
        <Switch.Control />
        {/* <Switch.Label>Activate Chakra</Switch.Label> */}
      </Switch.Root>
    );
  }
  console.log("user_id", localStorage.getItem("user_id"));
  return (
    <PageContent>
      <PageTitle icon={LuUsers} title="Account Management" />
      
      <Popover.Root open={isOpen} onOpenChange={(e) => setIsOpen(e.open)}>
        <Popover.Trigger asChild>
          <Flex>
          <Button size="sm" variant="solid">
            <LuUserPlus strokeWidth="2.5" />
            Create New Account
          </Button>
          </Flex>
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner>
            <Popover.Content>
              <Popover.Arrow />
              <Popover.Body>
                <Popover.Title fontWeight="medium">Create New User</Popover.Title>
                <Box my="4" display="flex" flexDirection="column" gap={3}>
                  <Input 
                    placeholder="Email" 
                    size="sm"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                  />
                  <Input 
                    placeholder="Password" 
                    type="password"
                    size="sm"
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                  />
                  <Button 
                    size="sm" 
                    onClick={handleCreateUser}
                    isDisabled={!newUserEmail || !newUserPassword}
                  >
                    Create User
                  </Button>
                </Box>
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
    
      <Table.Root variant="line" stickyHeader w="100%">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>ID</Table.ColumnHeader>
            <Table.ColumnHeader>Email</Table.ColumnHeader>
            <Table.ColumnHeader>Superuser</Table.ColumnHeader>
            <Table.ColumnHeader>Project Access</Table.ColumnHeader>
            <Table.ColumnHeader>Delete</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {users.map((user, index) => (
            <Table.Row key={user.id} 
              bg={index % 2 === 0 ? 'gray.subtle' : 'brand.bg'}
              _hover={{ bg: 'brand.100'}}
              >
                <Table.Cell><Box maxW="5.5em" maxH="1.5em" truncate>{user.id}</Box></Table.Cell>
                <Table.Cell>{user.email}</Table.Cell>
                <Table.Cell><SuperuserToggle userIndex={index} /></Table.Cell>
                <Table.Cell><ProjectSelect userIndex={index} /></Table.Cell>
                <Table.Cell  pl="2em" py="0" pr="0">
                  {user.id !== localStorage.getItem("user_id") &&
                    <Button variant="plain" size="md" maxH="2.2em" maxW="0.1em" color="red" _hover={{ bg: 'brand.subtle' }}
                      onClick={(e) => handleDelete(e, index)}
                      aria-label="Delete user"
                    >
                      <LuRemove strokeWidth="2.2"/>
                    </Button>
                  }
                  
                </Table.Cell>
              </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </PageContent>
  );
}

export default UserManagementPage;